import { useEffect, useState } from "react";

/**
 * Naljuri Inn — Logo Intro Animation
 *
 * Blends two motion ideas commonly seen in branding reels:
 *   1) Ink-bleed mask reveal: a soft cream blot expands from centre, peeling
 *      back the forest backdrop (organic, watercolour-esque).
 *   2) Hexagon stroke-draw + gold shimmer wordmark: the brand mark draws
 *      itself line-by-line; then a gold light streak sweeps across the
 *      "Naljuri" wordmark before everything fades away.
 *
 * Plays once per browser session (session-storage gated). Tap anywhere to skip.
 */
export default function LogoIntro() {
  const [phase, setPhase] = useState("ink");      // ink → draw → shimmer → out → done
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("naljuri_intro_played") === "1") {
      setPhase("done");
      return;
    }
    setMounted(true);
    const t1 = setTimeout(() => setPhase("draw"),    800);
    const t2 = setTimeout(() => setPhase("shimmer"), 1700);
    const t3 = setTimeout(() => setPhase("out"),     2700);
    const t4 = setTimeout(() => {
      setPhase("done");
      sessionStorage.setItem("naljuri_intro_played", "1");
    }, 3300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, []);

  const skip = () => {
    sessionStorage.setItem("naljuri_intro_played", "1");
    setPhase("done");
  };

  if (!mounted || phase === "done") return null;

  return (
    <div
      role="presentation"
      onClick={skip}
      data-testid="logo-intro"
      className={`fixed inset-0 z-[100] flex items-center justify-center cursor-pointer
        transition-opacity duration-500 ${phase === "out" ? "opacity-0" : "opacity-100"}`}
      style={{ background: "#0B1810" }}
    >
      {/* Ink-bleed cream blot — masks the backdrop with an organic radial */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: "radial-gradient(circle at 50% 50%, rgba(245,245,240,1) 0%, rgba(245,245,240,0.95) 35%, rgba(11,24,16,0) 60%)",
          transform: phase === "ink" ? "scale(0)" : "scale(1)",
          transition: "transform 1.1s cubic-bezier(0.19, 1, 0.22, 1)",
          filter: "blur(0.3px)",
        }}
      />

      {/* Logo — hexagon mark + wordmark */}
      <div className="relative flex flex-col items-center">
        <svg width="120" height="120" viewBox="0 0 40 40" fill="none"
             className="drop-shadow-[0_2px_18px_rgba(184,134,11,0.25)]"
             style={{
               transform: phase === "ink" ? "scale(0.4) rotate(-6deg)" : "scale(1) rotate(0deg)",
               transition: "transform 0.9s cubic-bezier(0.19, 1, 0.22, 1)",
             }}>
          {/* Hexagon outline — stroke-drawn */}
          <path
            d="M20 2 L36 12 V28 L20 38 L4 28 V12 Z"
            stroke="#1B3022"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 130,
              strokeDashoffset: phase === "ink" ? 130 : 0,
              transition: "stroke-dashoffset 0.9s ease-out 0.1s",
            }}
          />
          {/* Inner mark — drawn second */}
          <path
            d="M12 26 L18 18 L22 22 L28 14"
            stroke="#B8860B"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 40,
              strokeDashoffset: phase === "ink" || phase === "draw" ? 40 : 0,
              transition: "stroke-dashoffset 0.7s ease-out 0.2s",
              opacity: phase === "ink" ? 0 : 1,
            }}
          />
        </svg>

        {/* Wordmark with gold shimmer sweep */}
        <div className="mt-5 relative overflow-hidden">
          <div
            className="font-display text-3xl md:text-5xl tracking-tight text-forest-700"
            style={{
              opacity: phase === "ink" || phase === "draw" ? 0 : 1,
              transform: phase === "ink" || phase === "draw" ? "translateY(8px)" : "translateY(0)",
              transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
            }}
          >
            Naljuri
          </div>
          {/* Gold shimmer streak */}
          <div
            aria-hidden
            className="absolute inset-y-0 w-1/2 pointer-events-none"
            style={{
              background: "linear-gradient(100deg, transparent 30%, rgba(212,164,34,0.55) 50%, transparent 70%)",
              left: phase === "shimmer" || phase === "out" ? "120%" : "-60%",
              transition: "left 0.85s cubic-bezier(0.4, 0, 0.2, 1)",
              mixBlendMode: "soft-light",
            }}
          />
        </div>

        <div
          className="mt-3 text-[10px] uppercase tracking-[0.32em] text-gold-600"
          style={{
            opacity: phase === "shimmer" || phase === "out" ? 1 : 0,
            transition: "opacity 0.5s ease-out",
          }}
        >
          Inn · Sylhet
        </div>
      </div>

      {/* Subtle tap hint */}
      <div className="absolute bottom-8 text-[10px] uppercase tracking-[0.3em] text-forest-700/60">
        Tap to skip
      </div>
    </div>
  );
}
